import { useEffect, useMemo, useRef, useState } from "react";
import { Check, Loader2, Play, Search, Square } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import type { PickerOption } from "@/lib/liveavatar/options";
import {
  filterVoices,
  voiceFilterChoices,
  type VoiceFilters,
} from "@/lib/scenarios/providers/liveavatar/voiceSelection";

type Props = {
  label: string;
  value?: string;
  options: PickerOption[];
  loading?: boolean;
  error?: unknown;
  onChange: (id: string) => void;
  previewFetcher?: (o: PickerOption) => Promise<{ audio_base64: string; mime: string }>;
  languageName: (code?: string) => string | undefined;
};

/** One flat list: the selected voice first, the rest by name. */
function orderVoices(list: PickerOption[], selectedId?: string): PickerOption[] {
  const rest = list.filter((o) => o.id !== selectedId).sort((a, b) => a.label.localeCompare(b.label));
  const sel = list.find((o) => o.id === selectedId);
  return sel ? [sel, ...rest] : rest;
}

export function VoiceBrowserDialog({ label, value, options, loading, error, onChange, previewFetcher, languageName }: Props) {
  const [open, setOpen] = useState(false);
  const [filters, setFilters] = useState<VoiceFilters>({});
  const [playing, setPlaying] = useState<string | null>(null);
  const [loadingPreview, setLoadingPreview] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const selected = options.find((o) => o.id === value);
  const choices = useMemo(() => voiceFilterChoices(options, { language: filters.language }), [options, filters.language]);
  const visible = useMemo(() => filterVoices(options, filters, value), [options, filters, value]);
  const ordered = useMemo(() => orderVoices(visible, value), [visible, value]);
  const hasFilters = Boolean(filters.gender || filters.language || filters.accent || filters.search);

  const stop = () => {
    audioRef.current?.pause();
    audioRef.current = null;
    setPlaying(null);
  };

  useEffect(() => {
    if (open) setFilters({});
    else stop();
  }, [open]);

  const play = async (o: PickerOption) => {
    if (playing === o.id) return stop();
    stop();
    try {
      let src = o.preview_url;
      if (!src && previewFetcher) {
        setLoadingPreview(o.id);
        const r = await previewFetcher(o);
        src = `data:${r.mime};base64,${r.audio_base64}`;
      }
      if (!src) return;
      const a = new Audio(src);
      audioRef.current = a;
      a.onended = () => setPlaying(null);
      setPlaying(o.id);
      await a.play();
    } catch {
      setPlaying(null);
    } finally {
      setLoadingPreview(null);
    }
  };

  const canPreview = (o: PickerOption) => Boolean(o.preview_url || previewFetcher);

  return (
    <div>
      <label className="mb-1.5 block text-sm font-medium text-foreground">{label}</label>
      <div className="flex items-center gap-3 rounded-md border border-input bg-background px-3 py-2">
        <div className="min-w-0 flex-1">
          {loading ? (
            <span className="text-sm text-muted-foreground">Loading voices…</span>
          ) : selected ? (
            <>
              <div className="truncate text-sm font-medium text-foreground">{selected.label}</div>
              {selected.sublabel && <div className="truncate text-xs text-muted-foreground">{selected.sublabel}</div>}
            </>
          ) : value ? (
            <span className="text-sm text-muted-foreground">Saved voice ({value})</span>
          ) : (
            <span className="text-sm text-muted-foreground">No voice chosen</span>
          )}
        </div>
        <Button type="button" variant="outline" size="sm" onClick={() => setOpen(true)} disabled={loading}>
          {value ? "Change voice" : "Choose voice"}
        </Button>
      </div>
      {Boolean(error) && <p className="mt-1 text-xs text-destructive">Couldn't load voices.</p>}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="flex h-[min(85vh,720px)] max-w-2xl flex-col gap-4">
          <DialogHeader>
            <DialogTitle>Choose a voice</DialogTitle>
          </DialogHeader>

          <div className="space-y-2">
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                autoFocus
                placeholder="Search by name, accent or language"
                value={filters.search ?? ""}
                onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value || undefined }))}
                className="pl-8"
              />
            </div>
            <div className="flex flex-wrap items-center gap-2 text-xs">
              {([
                ["gender", "Gender", choices.genders],
                ["language", "Language", choices.languages],
                ["accent", "Accent", choices.accents],
              ] as const).map(([key, title, values]) => (
                <select
                  key={key}
                  aria-label={`Filter voices by ${title.toLowerCase()}`}
                  value={filters[key] ?? ""}
                  onChange={(e) =>
                    setFilters((f) => ({
                      ...f,
                      [key]: e.target.value || undefined,
                      ...(key === "language" ? { accent: undefined } : {}),
                    }))
                  }
                  className="h-8 rounded-md border border-input bg-background px-2 text-foreground"
                >
                  <option value="">{title}: All</option>
                  {values.map((v) => (
                    <option key={v} value={v}>
                      {key === "language" ? languageName(v) ?? v : v}
                    </option>
                  ))}
                </select>
              ))}
              {hasFilters && (
                <button type="button" onClick={() => setFilters({})} className="text-muted-foreground underline hover:text-foreground">
                  Clear filters
                </button>
              )}
              <span className="ml-auto text-muted-foreground">
                {visible.length} of {options.length} voices
              </span>
            </div>
          </div>

          <div className="-mx-2 min-h-0 flex-1 overflow-y-auto px-2">
            {visible.length === 0 ? (
              <div className="py-10 text-center text-sm text-muted-foreground">
                No voices match.{" "}
                <button type="button" className="underline hover:text-foreground" onClick={() => setFilters({})}>
                  Clear filters
                </button>
              </div>
            ) : (
              [ordered].map((items) => {
                return (
                  <div key="all" className="mb-3">
                    <ul className="space-y-1">
                      {items.map((o) => {
                        const isSel = o.id === value;
                        return (
                          <li key={o.id}>
                            <div
                              role="button"
                              tabIndex={0}
                              onClick={() => {
                                onChange(o.id);
                                setOpen(false);
                              }}
                              onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                  e.preventDefault();
                                  onChange(o.id);
                                  setOpen(false);
                                }
                              }}
                              className={`flex cursor-pointer items-center gap-3 rounded-md border px-3 py-2 transition-colors hover:bg-accent ${
                                isSel ? "border-primary bg-accent" : "border-transparent"
                              }`}
                            >
                              <div className="min-w-0 flex-1">
                                {isSel && <div className="text-[10px] font-semibold uppercase tracking-wide text-primary">Selected voice</div>}
                                <div className="truncate text-sm font-medium text-foreground">{o.label}</div>
                                {o.sublabel && <div className="truncate text-xs text-muted-foreground">{o.sublabel}</div>}
                              </div>
                              {canPreview(o) && (
                                <Button
                                  type="button"
                                  size="icon"
                                  variant="ghost"
                                  aria-label={playing === o.id ? "Stop preview" : "Play preview"}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    void play(o);
                                  }}
                                >
                                  {loadingPreview === o.id ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                  ) : playing === o.id ? (
                                    <Square className="h-4 w-4" />
                                  ) : (
                                    <Play className="h-4 w-4" />
                                  )}
                                </Button>
                              )}
                              {isSel && <Check className="h-4 w-4 text-primary" />}
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                );
              })
            )}
          </div>

          <div className="flex justify-end">
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
